<template>
    <v-container class="px-0" fluid>
        <v-card class="card-style px-1" outlined elevation="2">
            <v-card-title class="d-flex flex-nowrap pa-0 justify-center align-center">
                <!-- Título de la sección -->
                <p class="text-h5 text-truncate mb-5 mr-1">{{ $t('activeSignals.title') }}</p>
                <!-- Ícono de ayuda -->
                <v-tooltip bottom open-on-click max-width="180">
                    <template v-slot:activator="{ on }">
                        <div v-on="on" icon color="grey" class="mb-5 mr-2" size="10">
                            <v-icon size="15">mdi-help-circle</v-icon>
                        </div>
                    </template>
                    <span style="justify-content: center !important">{{ $t('activeSignals.help') }}</span>
                </v-tooltip>
                <!-- Botón para mostrar/ocultar los filtros -->
                <v-btn
                    v-if="!loadingActiveSignals"
                    class="mb-5"
                    icon
                    @click="toggleFilters">
                    <v-icon>{{ filtersVisible ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
            </v-card-title>

            <!-- Filtros -->
            <v-slide-y-transition v-if="!loadingActiveSignals">
                <v-card-subtitle v-if="filtersVisible" class="elevation-2 mx-4 mt-2">
                    <v-row class="pt-2">
                        <v-col cols="12" md="4" class="pb-0">
                            <v-text-field
                                v-model="filters.tickerName"
                                :label="$t('activeSignals.filter.tickerName')"
                                outlined
                                dense
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" md="4" class="pb-0">
                            <v-select
                                v-model="filters.sector"
                                :items="getSectors"
                                :label="$t('activeSignals.filter.sector')"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                        <v-col cols="12" md="4" class="py-0">
                            <v-dialog ref="dialog" v-model="calendarModal" :return-value.sync="filters.rangeDates" persistent width="290px">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field 
                                        v-model="filters.rangeDatesFormatted" 
                                        :label="$t('activeSignals.filter.signalDate')" 
                                        @blur="filters.rangeDates = datePickerparseDate(filters.rangeDatesFormatted)" 
                                        prepend-icon="mdi-calendar" 
                                        v-bind="attrs"
                                        v-on="on">
                                    </v-text-field>
                                </template>
                                <v-date-picker v-model="filters.rangeDates" :min="filters.minDate" scrollable range>
                                    <v-spacer></v-spacer>
                                    <v-btn text color="primary" @click="calendarModal = false"> {{ $t('general.cancel') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialog.save([])"> {{ $t('general.clear') }} </v-btn>
                                    <v-btn text color="primary" @click="$refs.dialog.save(filters.rangeDates)"> {{ $t('general.ok') }} </v-btn>
                                </v-date-picker>
                            </v-dialog>
                        </v-col>
                        <v-col cols="12" md="6" class="pb-0">
                            <v-range-slider v-model="filters.signalPriceRange" :min="getMinPriceAvailable" :max="getMaxPriceAvailable" :step="1" strict thumb-label :label="$t('activeSignals.filter.signalPrice')">
                                <template v-slot:thumb-label="{ value }">
                                    <span>{{ formatNumber(value, false) }}</span>
                                </template>
                            </v-range-slider>
                        </v-col>
                        <v-col cols="12" md="6" class="pb-0">
                            <v-range-slider v-model="filters.signalVolumeRange" :min="getMinVolumeAvailable" :max="getMaxVolumeAvailable" :step="1" strict thumb-label :label="$t('activeSignals.filter.signalVolume')">
                                <template v-slot:thumb-label="{ value }">
                                    <span>{{ formatLargeNumber(value) }}</span>
                                </template>
                            </v-range-slider>
                        </v-col>
                    </v-row>
                </v-card-subtitle>
            </v-slide-y-transition>

            <!-- Tabla -->
            <v-skeleton-loader
                v-if="loadingActiveSignals"
                type="image"
                class="height-450"
            ></v-skeleton-loader>
            <v-card-text v-else class="mt-4">
                <v-data-table
                    :headers="headers"
                    :items="filteredItems"
                    :sort-by.sync="filters.sortBy"
                    :sort-desc.sync="filters.sortDesc"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50],
                        'items-per-page-text': $t('activeSignals.table.footer.pageable')
                    }"
                    :items-per-page="(isMobileDevice() ? 5 : 10)"
                    class="material-table elevation-1"
                    dense
                    item-value="id"
                    :no-data-text="$t('activeSignals.table.empty')"
                    :custom-sort="tableSortConfiguration" >

                    <!-- Nombre con enlace -->
                    <template v-slot:item.ticker="{ item }">
                        <a @click="pushSignalDetailPage(item.id)">{{ item.ticker }}</a>
                    </template>
                    <template v-slot:item.signalPrice="{ item }">
                        <span>$ {{ item.signalPrice }}</span>
                    </template>
                    <template v-slot:item.volume="{ item }">
                        <div class="same-line-flex justify-center">
                            <span class="min-width-50">{{ formatNumber(calculateVolumeCompletion(item.breakoutVolume, item.signalVolume), true) }}%</span>
                            <v-progress-circular
                                :key="'vol-'+item.id"
                                :value="calculateVolumeCompletion(item.breakoutVolume, item.signalVolume)"
                                color="primary"
                                class="ml-2"
                            ></v-progress-circular>
                        </div>
                    </template>
                    <template v-slot:item.status="{ item }">
                        <div v-if="item.price >= item.signalPrice" class="text-center text-no-wrap">
                            {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="success">mdi-arrow-top-right</v-icon>
                        </div>
                        <div v-else class="text-center text-no-wrap">
                            {{calculateGrowthPercentageFormatted(item.price, item.signalPrice)}}% <v-icon color="error">mdi-arrow-bottom-right</v-icon>
                        </div>
                    </template>
                    <template v-slot:item.technical="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.technical)">
                                {{item.indicators.technical}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.fundamental="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.fundamental)">
                                {{item.indicators.fundamental}}%
                            </v-chip>
                          </div>
                    </template>
                    <template v-slot:item.strength="{ item }">
                        <div class="my-2">
                            <v-chip link :color="getCustomIndicatorsColor(item.indicators.strength)">
                                {{item.indicators.strength}}%
                            </v-chip>
                          </div>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
        <BasicModal :modalCode="modalCode"></BasicModal>
    </v-container>
  </template>

<script>
    import axios from 'axios';

    import { UtilsMixin } from '@/mixins/utils';

    import BasicModal from '@/components/Modal/BasicModal';

    export default {
        name: 'ActiveSignals',
        props: ['lastSessionDate'],
        mixins: [ 
            UtilsMixin 
        ],
        components: {
            BasicModal
        },
        data() {
            return {
                // Filtros
                filtersVisible: false,
                // Datos de la tabla
                headers: [
                    { text: this.$t('activeSignals.table.ticker'), value: "ticker" },
                    { text: this.$t('activeSignals.table.sector'), value: "sector" },
                    { text: this.$t('activeSignals.table.signalPrice'), value: "signalPrice" },
                    { text: this.$t('activeSignals.table.signalVolume'), value: "volume", align: 'center' },
                    { text: this.$t('activeSignals.table.status'), value: "status", align: 'center' },
                    { text: this.$t('activeSignals.table.technical'), value: "technical", align: 'center' },
                    { text: this.$t('activeSignals.table.fundamental'), value: "fundamental", align: 'center' },
                    { text: this.$t('activeSignals.table.strength'), value: "strength", align: 'center' },
                ],
                calendarModal: false,
                modalCode: '',
                activeSignals: [],
                loadingActiveSignals: true
            };
        },
        computed: {
            filters: {
                get() {
                    return this.$store.getters["pageFilters/getObject"]("activeSignalsFilters");
                },
                set(newValue) {
                    this.$store.commit("pageFilters/SET_OBJECT", { objectKey: "activeSignalsFilters", newObject: newValue });
                },
            },
            getSectors() {
                if (this.activeSignals.length == 0) return [];

                return this.filters.sector ? ["", ...new Set(this.activeSignals.map(s => s.sector))].sort()
                        : [...new Set(this.activeSignals.map(s => s.sector))].sort();
            },
            getMaxPriceAvailable() {
                if (this.activeSignals.length == 0) return 100;

                let value = this.activeSignals.reduce((max, item) => {
                    return item.signalPrice > max ? item.signalPrice : max;
                }, 0);
                return Math.round(value + 1);
            },
            getMinPriceAvailable() {
                if (this.activeSignals.length == 0) return 0;

                let value = this.activeSignals.reduce((min, item) => {
                    return item.signalPrice < min ? item.signalPrice : min;
                }, Infinity);
                return Math.round(value - 1);
            },
            getMaxVolumeAvailable() {
                if (this.activeSignals.length == 0) return 100000;

                let value = this.activeSignals.reduce((max, item) => {
                    return item.signalVolume > max ? item.signalVolume : max;
                }, 0);
                return value + 1;
            },
            getMinVolumeAvailable() {
                if (this.activeSignals.length == 0) return 0;

                let value = this.activeSignals.reduce((min, item) => {
                    return item.signalVolume < min ? item.signalVolume : min;
                }, Infinity);
                return value - 1;
            },
            filteredItems() {
                if (this.activeSignals.length == 0) return [];

                return this.activeSignals.filter(item => 
                    // Filtro por ticker y nombre
                    (!this.filters.tickerName || (item.ticker.toLowerCase().includes(this.filters.tickerName.toLowerCase()) || item.name.toLowerCase().includes(this.filters.tickerName.toLowerCase())))
                    // Filtro por sector
                    && (!this.filters.sector || (item.sector.toLowerCase().includes(this.filters.sector.toLowerCase()) ))
                    // Filtro por precio de la señal
                    && (item.signalPrice >= this.filters.signalPriceRange[0] && item.signalPrice <= this.filters.signalPriceRange[1])
                    // Filtro por volumen de la señal
                    && (item.signalVolume >= this.filters.signalVolumeRange[0] && item.signalVolume <= this.filters.signalVolumeRange[1])
                    // Filtro por rango de fechas
                    && ((!this.filters.rangeDates || this.filters.rangeDates.length == 0)
                        || (this.filters.rangeDates.length == 1 && new Date(this.filters.rangeDates[0]).getTime() == new Date(item.signalDate).getTime())
                        || (this.filters.rangeDates.length == 2 && new Date(this.filters.rangeDates[0]).getTime() <= new Date(item.signalDate).getTime() && new Date(this.filters.rangeDates[1]).getTime() >= new Date(item.signalDate).getTime())
                    )
                );
            }
        },
        watch: {
            'filters.rangeDates' () {
                this.filters.rangeDatesFormatted = this.datePickerformatDate(this.filters.rangeDates);
                this.filters.minDate = this.filters.rangeDates && this.filters.rangeDates.length == 1 ? this.datePickerparseDate(this.filters.rangeDatesFormatted)[0] : null;
            },
        },
        created() {
            // Se muestran los filtros si se accede desde desktop
            this.filtersVisible = this.isMobileDevice() ? false : true;

            // Si viene del componente de última sesión se añade la fecha en el filtro para mostrar los registros de ese día
            if (this.lastSessionDate) {
                this.filters.rangeDates = [this.lastSessionDate];

                // Se resetean el resto de filtros en este caso
                this.filters.tickerName = null;
                this.filters.sector = null;
                this.filters.signalPriceRange = [];
                this.filters.signalVolumeRange = [];
            }
        },
        mounted() {
            // API calls
            this.getActiveSignals();
        },
        methods: {
            // ---- FUNCIONES UTILIZADAS POR LOS COMPONENTES EN PANTALLA  ----
            // Permite mostrar u ocultar las opciones de filtrado
            toggleFilters() {
                this.filtersVisible = !this.filtersVisible;
            },
            // Control para ordenar los resultados en la tabla de señales
            tableSortConfiguration(items, sortBy, descending) {
                if (sortBy.length > 0) {
                    if (sortBy[0] === "volume") {
                        items.sort((a, b) => {
                            const scoreA = this.calculateVolumeCompletion(a.breakoutVolume, a.signalVolume)
                            const scoreB = this.calculateVolumeCompletion(b.breakoutVolume, b.signalVolume)

                            if (typeof scoreA === "number" && typeof scoreB === "number") {
                                return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                            } else {
                                return descending[0] ? scoreB.toString().localeCompare(scoreA.toString()) : scoreA.toString().localeCompare(scoreB.toString());
                            }
                        });
                    } else if (sortBy[0] === "status") {
                        items.sort((a, b) => {
                            const scoreA = this.calculateGrowthPercentage(a.price, a.signalPrice)
                            const scoreB = this.calculateGrowthPercentage(b.price, b.signalPrice)

                            if (typeof scoreA === "number" && typeof scoreB === "number") {
                                return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                            } else {
                                return descending[0] ? scoreB.toString().localeCompare(scoreA.toString()) : scoreA.toString().localeCompare(scoreB.toString());
                            }
                        });
                    } else if (sortBy[0] === "technical" || sortBy[0] === "fundamental" || sortBy[0] === "strength") {
                        items.sort((a, b) => {
                            const scoreA = a["indicators"][sortBy[0]]
                            const scoreB = b["indicators"][sortBy[0]]

                            return descending[0] ? scoreB - scoreA : scoreA - scoreB;
                        });
                    } else {
                        items.sort((a, b) => {
                            const valA = a[sortBy[0]];
                            const valB = b[sortBy[0]];

                            if (typeof valA === "number" && typeof valB === "number") {
                                return descending[0] ? valB - valA : valA - valB;
                            } else {
                                return descending[0] ? valB.toString().localeCompare(valA.toString()) : valA.toString().localeCompare(valB.toString());
                            }
                        });
                    }
                }
                return items;
            },
            // Acceso al detalle de una señal
            pushSignalDetailPage(id) {
                this.$router.push({ name: 'SignalDetail', params: { id: id } });
            },
            // Configura los máximos en los filtros de rangos
            setupMaxAndMinFilters() {
                // Se inicializan los filtros de rangos con todos los valores asignados
                if (this.filters.signalPriceRange.length == 0 && this.filters.signalVolumeRange.length == 0) {
                    this.filters.signalPriceRange = [this.getMinPriceAvailable, this.getMaxPriceAvailable];
                    this.filters.signalVolumeRange = [this.getMinVolumeAvailable, this.getMaxVolumeAvailable];
                }
            },
            showErrorModal() {
                this.modalCode = 'apiErrorModal';
                this.showDefaultErrorModal(this.modalCode);
            },
            // ---- PETICIONES AL BACKEND ----
            // Consulta el listado de señales activas
            async getActiveSignals() {
                this.loadingActiveSignals = true;
                try {
                    const response = await axios.get('/signal/getActiveSignals');
                    this.activeSignals = response.data;
                    this.setupMaxAndMinFilters();
                } catch (e) {
                    this.showErrorModal();
                } finally {
                    this.loadingActiveSignals = false;
                }
            },
        }
    };
</script>

<style scoped>
</style>